// AdminEvents.jsx - Etkinlik yönetimi component'i
import React, { useState } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import EventForm from './EventForm';
import useEvents from '../../hooks/useEvents';

const AdminEvents = () => {
  const { events, loading, error, addEvent, editEvent, removeEvent } = useEvents();
  
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const handleCreateEvent = async (eventData) => {
    try {
      setFormLoading(true);
      await addEvent(eventData);
      setShowForm(false);
      setSuccessMessage('Etkinlik başarıyla oluşturuldu!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error creating event:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateEvent = async (eventData) => {
    try {
      setFormLoading(true);
      await editEvent(editingEvent.id, eventData);
      setEditingEvent(null);
      setShowForm(false);
      setSuccessMessage('Etkinlik başarıyla güncellendi!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating event:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await removeEvent(eventId);
      setDeleteConfirm(null);
      setSuccessMessage('Etkinlik başarıyla silindi!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting event:', err);
    }
  };

  const openEditForm = (event) => {
    setEditingEvent(event);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingEvent(null);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      karaoke: 'Karaoke',
      parti: 'Parti',
      söyleşi: 'Söyleşi',
      konser: 'Konser',
      atölye: 'Atölye',
      workshop: 'Workshop',
    };
    return labels[category?.toLowerCase()] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      karaoke: 'bg-pink-600 text-white',
      parti: 'bg-purple-600 text-white',
      söyleşi: 'bg-blue-600 text-white',
      konser: 'bg-green-600 text-white',
      atölye: 'bg-yellow-600 text-white',
      workshop: 'bg-orange-600 text-white',
    };
    return colors[category?.toLowerCase()] || 'bg-gray-600 text-white';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center">
              <svg className="w-8 h-8 mr-3 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Etkinlik Yönetimi
            </h2>
            <p className="text-gray-300 mt-2">Etkinlikleri ekleyin, düzenleyin veya silin</p>
          </div>
          
          {!showForm && (
            <Button
              onClick={() => setShowForm(true)}
              className="flex items-center bg-indigo-600 hover:bg-indigo-500"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Yeni Etkinlik
            </Button>
          )}
        </div>
      </Card>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-900/80 border border-green-500 text-green-200 px-6 py-4 rounded-lg shadow-lg flex items-center">
          <svg className="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/80 border border-red-500 text-red-200 px-6 py-4 rounded-lg shadow-lg flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}

      {/* Event Form */}
      {showForm && (
        <Card>
          <h3 className="text-xl font-semibold text-white mb-6">
            {editingEvent ? 'Etkinliği Düzenle' : 'Yeni Etkinlik Oluştur'}
          </h3>
          <EventForm
            event={editingEvent}
            onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
            onCancel={closeForm}
            loading={formLoading}
          />
        </Card>
      )}

      {/* Events List */}
      <Card>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">
            Tüm Etkinlikler ({events.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-400 text-lg">Henüz etkinlik bulunmuyor</p>
            <p className="text-gray-500 mt-2">İlk etkinliğinizi oluşturmak için yukarıdaki butona tıklayın</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Etkinlik</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Tarih</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Konum</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Kategori</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Fiyat</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-300">Durum</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-gray-300">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {events.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        {event.image && (
                          <img
                            src={event.image}
                            alt={event.title}
                            className="w-12 h-12 rounded-lg object-cover mr-3"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <p className="font-medium text-gray-100">{event.title}</p>
                          <p className="text-sm text-gray-400 truncate max-w-xs">
                            {event.description?.substring(0, 50)}...
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <p className="text-gray-200">{formatDate(event.date)}</p>
                      <p className="text-sm text-gray-400">{event.time}</p>
                    </td>
                    <td className="py-4 px-4 text-gray-300">{event.location}</td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(event.category)}`}>
                        {getCategoryLabel(event.category)}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-gray-300">{event.price || 'Ücretsiz'}</td>
                    <td className="py-4 px-4">
                      {event.featured ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-indigo-600 text-white">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Öne Çıkan
                        </span>
                      ) : (
                        <span className="text-gray-500 text-sm">Normal</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => openEditForm(event)}
                          className="p-2 text-blue-400 hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(event.id)}
                          className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6 border border-gray-700">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-900/50 flex items-center justify-center mr-4 border border-red-700">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Etkinliği Sil</h3>
                <p className="text-gray-400">Bu işlem geri alınamaz.</p>
              </div>
            </div>
            <p className="text-gray-300 mb-6">
              Bu etkinliği silmek istediğinizden emin misiniz? Tüm etkinlik bilgileri kalıcı olarak silinecektir.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-200 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors font-medium"
              >
                İptal
              </button>
              <button
                onClick={() => handleDeleteEvent(deleteConfirm)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvents;
